import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMicrophone, FaMicrophoneSlash, FaVolumeUp, FaVolumeMute, FaTimes } from 'react-icons/fa';
import './VoiceAssistant.scss';
import analyticsService from '../../services/analyticsService';

const VoiceAssistant = ({ onVoiceCommand, onVoiceQuestion, onClose }) => {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [isSupported, setIsSupported] = useState(false);
    const [showSettings, setShowSettings] = useState(true);
    const [isListening, setIsListening] = useState(false);
    const [availableVoices, setAvailableVoices] = useState([]);
    const [voiceSettings, setVoiceSettings] = useState({
        rate: 1.0,
        pitch: 1.0,
        volume: 1.0,
        voice: null
    });
    
    const recognitionRef = useRef(null);
    const synthesisRef = useRef(null);
    const isListeningRef = useRef(false);
    const lastSpokenTimeRef = useRef(0); // Track when we last spoke

    // Initialize speech recognition and synthesis
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const SpeechSynthesis = window.speechSynthesis;

        if (SpeechRecognition && SpeechSynthesis) {
            setIsSupported(true);
            
            // Initialize speech recognition
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'en-US';

            // Speech recognition event handlers
            recognitionRef.current.onstart = () => {
                console.log('✅ Voice recognition started');
                isListeningRef.current = true;
                setIsListening(true);
            };

            recognitionRef.current.onresult = (event) => {
                // Don't process results if we're not supposed to be listening
                if (!isListeningRef.current) {
                    console.log('🔄 Ignoring speech result - not listening');
                    return;
                }

                let finalTranscript = '';
                let interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript;
                    } else {
                        interimTranscript += transcript;
                    }
                }

                setTranscript(finalTranscript || interimTranscript);

                // Process final transcript only if we're still listening
                if (finalTranscript && isListeningRef.current) {
                    console.log('🎤 Processing command:', finalTranscript);
                    processVoiceCommand(finalTranscript);
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('❌ Speech recognition error:', event.error);
                // Only reset state if it's not a user-initiated stop
                if (event.error !== 'no-speech' && event.error !== 'aborted') {
                    isListeningRef.current = false;
                    setIsListening(false);
                }
            };

            recognitionRef.current.onend = () => {
                console.log('🔄 Voice recognition ended');
                // Only restart if we're still supposed to be listening
                if (isListeningRef.current) {
                    // If we're still supposed to be listening, restart
                    try {
                        console.log('🔄 Restarting voice recognition...');
                        recognitionRef.current.start();
                    } catch (error) {
                        console.log('❌ Error restarting recognition:', error);
                        isListeningRef.current = false;
                        setIsListening(false);
                    }
                } else {
                    // Manually stopped, don't restart
                    console.log('✅ Voice recognition manually stopped');
                    isListeningRef.current = false;
                    setIsListening(false);
                }
            };

            // Initialize speech synthesis
            synthesisRef.current = window.speechSynthesis;
            
            // Check if speech synthesis is available
            if (synthesisRef.current) {
                console.log('✅ Speech synthesis initialized successfully');
                // Get available voices
                const voices = synthesisRef.current.getVoices();
                console.log('🎤 Available voices:', voices.length);
                setAvailableVoices(voices);
                
                // Handle voices loading asynchronously
                if (voices.length === 0) {
                    synthesisRef.current.onvoiceschanged = () => {
                        const loadedVoices = synthesisRef.current.getVoices();
                        console.log('🎤 Voices loaded asynchronously:', loadedVoices.length);
                        setAvailableVoices(loadedVoices);
                    };
                }
            } else {
                console.error('❌ Speech synthesis not available');
            }
        } else {
            setIsSupported(false);
            console.warn('Speech recognition not supported in this browser');
        }

        // Cleanup function
        return () => {
            console.log('🛑 Cleaning up voice assistant on unmount...');
            
            if (recognitionRef.current && isListeningRef.current) {
                try {
                    recognitionRef.current.stop();
                    console.log('✅ Stopped speech recognition during cleanup');
                } catch (error) {
                    console.log('Error stopping recognition during cleanup:', error);
                }
            }
            
            // Clean up speech synthesis
            if (synthesisRef.current) {
                try {
                    synthesisRef.current.cancel();
                    synthesisRef.current.pause();
                    synthesisRef.current.resume();
                    synthesisRef.current.cancel();
                    console.log('✅ Cleaned up speech synthesis on unmount');
                } catch (error) {
                    console.log('Error cleaning up speech synthesis:', error);
                }
            }
            
            // Reset all refs
            isListeningRef.current = false;
            lastSpokenTimeRef.current = 0;
            
            console.log('✅ Voice assistant cleanup completed');
        };
    }, []);

    // Process voice commands
    const processVoiceCommand = (command) => {
        // Don't process commands if we're not listening
        if (!isListeningRef.current) {
            console.log('🔄 Ignoring command - not listening:', command);
            return;
        }

        const lowerCommand = command.toLowerCase().trim();
        console.log('🎤 Processing voice command:', lowerCommand);
        
        // Track voice interaction
        analyticsService.trackVoiceInteraction(command, 'received');
        
        // Navigation commands
        if (lowerCommand.includes('go to') || lowerCommand.includes('navigate to')) {
            if (lowerCommand.includes('about')) {
                analyticsService.trackVoiceInteraction(command, 'navigation_to_about');
                onVoiceCommand('navigate', '#about');
            } else if (lowerCommand.includes('skills')) {
                analyticsService.trackVoiceInteraction(command, 'navigation_to_skills');
                onVoiceCommand('navigate', '#skills');
            } else if (lowerCommand.includes('projects')) {
                analyticsService.trackVoiceInteraction(command, 'navigation_to_projects');
                onVoiceCommand('navigate', '#projects');
            } else if (lowerCommand.includes('contact')) {
                analyticsService.trackVoiceInteraction(command, 'navigation_to_contact');
                onVoiceCommand('navigate', '#contact');
            } else if (lowerCommand.includes('home')) {
                analyticsService.trackVoiceInteraction(command, 'navigation_to_home');
                onVoiceCommand('navigate', '#home');
            }
        }
        
        // AI Chat commands - only specific phrases
        else if (lowerCommand.includes('ask') || lowerCommand.includes('question') || lowerCommand.includes('chat') || lowerCommand.includes('ai assistant') || lowerCommand.includes('ai chat')) {
            analyticsService.trackVoiceInteraction(command, 'ai_chat_requested');
            onVoiceQuestion(command);
        }
        
        // Portfolio commands
        else if (lowerCommand.includes('resume') || lowerCommand.includes('cv')) {
            analyticsService.trackVoiceInteraction(command, 'resume_requested');
            onVoiceCommand('open', 'resume');
        }
        else if (lowerCommand.includes('github') || lowerCommand.includes('git hub')) {
            analyticsService.trackVoiceInteraction(command, 'github_requested');
            onVoiceCommand('open', 'github');
        }
        else if (lowerCommand.includes('linkedin') || lowerCommand.includes('linked in')) {
            analyticsService.trackVoiceInteraction(command, 'linkedin_requested');
            onVoiceCommand('open', 'linkedin');
        }
        else if (
            (lowerCommand.includes('huggingface')) ||
            (lowerCommand.includes('hugging face')) ||
            (lowerCommand.match(/hugging\s*face/))
        ) {
            analyticsService.trackVoiceInteraction(command, 'huggingface_requested');
            onVoiceCommand('open', 'huggingface');
        }
        
        // Help command
        else if (lowerCommand.includes('help') || lowerCommand.includes('commands')) {
            analyticsService.trackVoiceInteraction(command, 'help_requested');
            speakText("Here are some voice commands you can use: Say 'go to about' to navigate to the about section, 'chat with AI' to open the AI assistant, 'visit github' or 'open github' to visit my GitHub profile, 'visit linkedin' or 'open linkedin' to visit my LinkedIn profile, or 'help' to hear this message again.");
        }
        
        // For general speech, just acknowledge it without opening AI chat
        else {
            // Skip very short or unclear speech
            if (command.trim().length < 3) {
                console.log('🔄 Skipping very short speech:', command);
                return;
            }

            // Response for any unrecognized speech
            analyticsService.trackVoiceInteraction(command, 'unrecognized_command');
            speakText("Say 'help' to hear the available commands.");
        }
    };

    // Start/stop voice recognition
    const toggleListening = () => {
        if (!isSupported || !recognitionRef.current) return;

        try {
            if (isListeningRef.current) {
                // Stop listening
                console.log('🛑 Stopping voice recognition...');
                
                // Set listening state to false immediately to prevent processing
                isListeningRef.current = false;
                setIsListening(false);
                setTranscript(''); // Clear transcript when stopping
                
                // Stop the recognition
                recognitionRef.current.stop();
                
                console.log('✅ Listening state set to false');
                
                // Track voice assistant toggle
                analyticsService.trackButtonClick('voice_assistant_toggle', 'stopped');
            } else {
                // Start listening
                console.log('🎤 Starting voice recognition...');
                
                // Set listening state to true
                isListeningRef.current = true;
                setIsListening(true);
                
                // Start the recognition
                recognitionRef.current.start();
                
                console.log('✅ Listening state set to true');
                
                // Track voice assistant toggle
                analyticsService.trackButtonClick('voice_assistant_toggle', 'started');
            }
        } catch (error) {
            console.error('❌ Error toggling speech recognition:', error);
            // Reset state on error
            isListeningRef.current = false;
            setIsListening(false);
        }
    };

    // Text-to-speech function with cooldown
    const speakText = (text) => {
        if (!synthesisRef.current) {
            console.log('🔄 Speech synthesis not available, continuing without voice feedback');
            return;
        }

        // Check if we spoke recently (within 2 seconds)
        const now = Date.now();
        const timeSinceLastSpoken = now - lastSpokenTimeRef.current;
        
        if (timeSinceLastSpoken < 2000) {
            console.log('🔄 Skipping speech due to cooldown');
            return;
        }

        console.log('🗣️ Attempting to speak:', text);
        console.log('🎛️ Using voice settings:', voiceSettings);

        // Disable microphone while speaking
        if (isListeningRef.current) {
            console.log('🛑 Disabling microphone while speaking...');
            recognitionRef.current.stop();
            isListeningRef.current = false;
            setIsListening(false);
        }

        // Stop any current speech gently
        try {
            synthesisRef.current.cancel();
        } catch (error) {
            console.log('Error stopping previous speech:', error);
        }

        // Wait a brief moment before starting new speech
        setTimeout(() => {
            // Create new utterance
            const utterance = new SpeechSynthesisUtterance(text);
            
            // Apply voice settings
            utterance.rate = voiceSettings.rate;
            utterance.pitch = voiceSettings.pitch;
            utterance.volume = voiceSettings.volume;
            utterance.lang = 'en-US';

            // Set the selected voice if available
            if (voiceSettings.voice) {
                const selectedVoice = availableVoices.find(v => v.voiceURI === voiceSettings.voice);
                if (selectedVoice) {
                    utterance.voice = selectedVoice;
                    console.log('🎤 Using selected voice:', selectedVoice.name);
                }
            }

            console.log('🎤 Applied settings - Rate:', utterance.rate, 'Pitch:', utterance.pitch, 'Volume:', utterance.volume, 'Voice:', utterance.voice?.name || 'default');

            // Set up event handlers
            utterance.onstart = () => {
                console.log('🗣️ Started speaking:', text);
                console.log('🎤 Final settings - Rate:', utterance.rate, 'Pitch:', utterance.pitch, 'Volume:', utterance.volume, 'Voice:', utterance.voice?.name || 'default');
                setIsSpeaking(true);
                lastSpokenTimeRef.current = Date.now();
            };
            
            utterance.onend = () => {
                console.log('✅ Finished speaking');
                setIsSpeaking(false);
                
                // Re-enable microphone after speaking
                console.log('🎤 Re-enabling microphone after speaking...');
                isListeningRef.current = true;
                setIsListening(true);
                try {
                    recognitionRef.current.start();
                    console.log('✅ Microphone re-enabled');
                } catch (error) {
                    console.log('❌ Error re-enabling microphone:', error);
                }
            };
            
            utterance.onerror = (event) => {
                console.error('❌ Speech synthesis error:', event.error);
                console.error('❌ Error details:', event);
                setIsSpeaking(false);
                
                // Re-enable microphone on error
                console.log('🎤 Re-enabling microphone after speech error...');
                isListeningRef.current = true;
                setIsListening(true);
                try {
                    recognitionRef.current.start();
                    console.log('✅ Microphone re-enabled after error');
                } catch (error) {
                    console.log('❌ Error re-enabling microphone after error:', error);
                }
                
                // Don't show alert, just log the error
                if (event.error === 'canceled') {
                    console.log('🔄 Speech was canceled, this might be normal');
                } else {
                    console.log('🔄 Speech synthesis error occurred, but continuing normally');
                }
            };

            // Speak the text
            try {
                synthesisRef.current.speak(utterance);
                console.log('🎤 Speech synthesis started with settings applied');
            } catch (error) {
                console.error('❌ Error starting speech synthesis:', error);
                setIsSpeaking(false);
                console.log('🔄 Speech synthesis failed, but voice assistant continues to work');
                
                // Re-enable microphone on error
                console.log('🎤 Re-enabling microphone after speech failure...');
                isListeningRef.current = true;
                setIsListening(true);
                try {
                    recognitionRef.current.start();
                    console.log('✅ Microphone re-enabled after failure');
                } catch (error) {
                    console.log('❌ Error re-enabling microphone after failure:', error);
                }
            }
        }, 100); // Small delay to ensure previous speech is fully stopped
    };

    // Stop speaking
    const stopSpeaking = () => {
        if (synthesisRef.current) {
            try {
                synthesisRef.current.cancel();
                console.log('🛑 Stopped speech synthesis');
            } catch (error) {
                console.log('Error stopping speech synthesis:', error);
            }
        }
        setIsSpeaking(false);
    };

    // Completely stop voice assistant
    const stopVoiceAssistant = () => {
        console.log('🛑 Completely stopping voice assistant...');
        
        // Stop speech recognition if it's running
        if (recognitionRef.current && isListeningRef.current) {
            try {
                recognitionRef.current.stop();
                console.log('✅ Stopped speech recognition');
            } catch (error) {
                console.log('Error stopping recognition:', error);
            }
        }
        
        // Stop any ongoing speech synthesis
        if (synthesisRef.current) {
            try {
                synthesisRef.current.cancel();
                synthesisRef.current.pause();
                synthesisRef.current.resume();
                synthesisRef.current.cancel(); // Double cancel to ensure it stops
                console.log('✅ Stopped all speech synthesis');
            } catch (error) {
                console.log('Error stopping speech synthesis during cleanup:', error);
            }
        }
        
        // Reset all states completely
        isListeningRef.current = false;
        setIsListening(false);
        setIsSpeaking(false);
        setTranscript('');
        lastSpokenTimeRef.current = 0; // Reset cooldown timer
        
        // Reset voice settings to default
        setVoiceSettings({
            rate: 1.0,
            pitch: 1.0,
            volume: 1.0,
            voice: null
        });
        
        console.log('✅ All states reset to default');
        console.log('✅ Voice assistant completely stopped');
        
        // Call the onClose function
        onClose();
    };

    // Voice settings controls
    const updateVoiceSettings = (setting, value) => {
        console.log(`🎛️ Updating ${setting} to ${value}`);
        setVoiceSettings(prev => {
            const newSettings = {
                ...prev,
                [setting]: value
            };
            console.log('🎛️ New voice settings:', newSettings);
            return newSettings;
        });
    };

    if (!isSupported) {
        return (
            <motion.div 
                className="voice-assistant-unsupported"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
            >
                <p>Voice assistant is not supported in your browser.</p>
                <p>Please use Chrome, Edge, or Safari for voice features.</p>
            </motion.div>
        );
    }

    return (
        <div className="voice-assistant">
            {/* Main voice control button */}
            <motion.button
                className={`voice-control-btn ${isListening ? 'listening' : 'disabled'}`}
                onClick={toggleListening}
                disabled={!isSupported}
                whileHover={isSupported ? { scale: 1.1 } : {}}
                whileTap={isSupported ? { scale: 0.9 } : {}}
                title={isListening ? 'Click to stop listening' : isSupported ? 'Click to start listening' : 'Voice assistant not supported'}
            >
                {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
            </motion.button>

            {/* Status indicator */}
            <AnimatePresence>
                {isListening && (
                    <motion.div
                        className="voice-status"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                    >
                        <span className="status-text listening">
                            🎤 Listening...
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Voice transcript display */}
            <AnimatePresence>
                {transcript && (
                    <motion.div
                        className="voice-transcript"
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.9 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <p><strong>You said:</strong> {transcript}</p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Voice settings panel */}
            <motion.div 
                className="voice-settings"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.9 }}
                transition={{ 
                    duration: 0.4, 
                    ease: "easeOut",
                    type: "spring",
                    stiffness: 300,
                    damping: 25
                }}
            >
                <motion.div 
                    className="voice-settings-header"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                >
                    <h4>Voice Assistant</h4>
                    <motion.button 
                        className="close-voice-btn"
                        onClick={stopVoiceAssistant}
                        title="Close Voice Assistant"
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <FaTimes />
                    </motion.button>
                </motion.div>
                
                {/* Speech rate */}
                <motion.div 
                    className="setting-group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                >
                    <label>Speed:</label>
                    <input
                        type="range"
                        min="0.5"
                        max="2.0"
                        step="0.1"
                        value={voiceSettings.rate}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            console.log('🎛️ Speed slider changed to:', value);
                            updateVoiceSettings('rate', value);
                        }}
                    />
                    <span>{voiceSettings.rate}x</span>
                </motion.div>

                {/* Pitch */}
                <motion.div 
                    className="setting-group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                >
                    <label>Pitch:</label>
                    <input
                        type="range"
                        min="0.5"
                        max="2.0"
                        step="0.1"
                        value={voiceSettings.pitch}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            console.log('🎛️ Pitch slider changed to:', value);
                            updateVoiceSettings('pitch', value);
                        }}
                    />
                    <span>{voiceSettings.pitch}x</span>
                </motion.div>

                {/* Volume */}
                <motion.div 
                    className="setting-group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                >
                    <label>Volume:</label>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={voiceSettings.volume}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            console.log('🎛️ Volume slider changed to:', value);
                            updateVoiceSettings('volume', value);
                        }}
                    />
                    <span>{Math.round(voiceSettings.volume * 100)}%</span>
                </motion.div>

                {/* Voice selection */}
                <motion.div 
                    className="setting-group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                >
                    <label>Voice:</label>
                    <select
                        value={voiceSettings.voice}
                        onChange={(e) => {
                            const selectedVoice = e.target.value;
                            console.log('🎛️ Selected voice:', selectedVoice);
                            updateVoiceSettings('voice', selectedVoice);
                        }}
                    >
                        <option value={null}>Select a voice</option>
                        {availableVoices.map((voice) => (
                            <option key={voice.voiceURI} value={voice.voiceURI}>
                                {voice.name}
                            </option>
                        ))}
                    </select>
                </motion.div>

                {/* Control buttons */}
                <motion.div 
                    className="voice-controls"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.3 }}
                >
                    <motion.button
                        onClick={() => {
                            if (!isSpeaking) {
                                console.log('🔊 Test Voice button clicked');
                                console.log('🎤 Current voice settings:', voiceSettings);
                                speakText(`Testing voice settings. Speed is ${voiceSettings.rate}x, pitch is ${voiceSettings.pitch}x, and volume is ${Math.round(voiceSettings.volume * 100)}%. Voice assistant is ready. Say 'help' to hear available commands.`);
                            } else {
                                console.log('🔇 Already speaking, ignoring click');
                            }
                        }}
                        disabled={isSpeaking}
                        className="test-voice-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <FaVolumeUp /> Test Voice
                    </motion.button>
                    
                    {isSpeaking && (
                        <motion.button
                            onClick={stopSpeaking}
                            className="stop-voice-btn"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaVolumeMute /> Stop
                        </motion.button>
                    )}
                </motion.div>

                {/* Help section */}
                <motion.div 
                    className="voice-help"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.3 }}
                >
                    <h5>Voice Commands:</h5>
                    <ul>
                        <li>"Go to about" - Navigate to About section</li>
                        <li>"Go to skills" - Navigate to Skills section</li>
                        <li>"Go to projects" - Navigate to Projects section</li>
                        <li>"Go to contact" - Navigate to Contact section</li>
                        <li>"Chat with AI" - Open AI chat assistant</li>
                        <li>"Visit GitHub" or "Open GitHub" - Visit GitHub profile</li>
                        <li>"Visit LinkedIn" or "Open LinkedIn" - Visit LinkedIn profile</li>
                        <li>"Visit HuggingFace" or "Open Huggingface" - Visit Hugging Face profile</li>
                        <li>"Help" - Hear available commands</li>
                    </ul>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default VoiceAssistant; 